import React, {useEffect, useMemo, useRef, useState} from 'react';
import PrizeCard from 'app/components/reusable/PrizeCard';
import {lootRarity} from 'app/utils/lootboxRarity';
import {
    reelCopiesForEndIndex,
    reelEndIndex,
    reelStepPx,
    repeatItemsForReel,
    useReelSpin
} from 'app/utils/reelCarousel';
import LootInterface from 'app/interfaces/LootInterface';

const CARD_SIZE = 140;
const CARD_GAP = 8;
const CARD_STEP = reelStepPx(CARD_SIZE, CARD_GAP);
const START_INDEX = 8;
const ROTATIONS = 5;
const RIGHT_PADDING_CARDS = 6;
const SPIN_DELAY_MS = 3200;
const SPIN_DURATION_MS = 6000;
const SPIN_END_PAUSE_MS = 400;

export const INVENTORY_LOOTBOX_SPIN_DELAY_MS = 1800;

export const LOOTBOX_CAROUSEL_OPENING_MS = SPIN_DELAY_MS + SPIN_DURATION_MS + SPIN_END_PAUSE_MS;

export const lootboxCarouselResultDelayMs = (timeStart: number, spinDelayMs = SPIN_DELAY_MS): number => {
    const spinReadyAt = timeStart + spinDelayMs;
    const resultAt = Math.max(spinReadyAt, Date.now()) + SPIN_DURATION_MS + SPIN_END_PAUSE_MS;

    return Math.max(resultAt - Date.now(), 0);
};

interface Props {
    fading?: boolean
    loot: LootInterface[]
    spinDelayMs?: number
    winner?: LootInterface | null
}

const reelWithWinner = (
    loot: LootInterface[],
    winner: LootInterface | null | undefined,
    landingIndex: number
): LootInterface[] => {
    if (loot.length === 0) {
        return [];
    }

    const copies = repeatItemsForReel(
        loot,
        reelCopiesForEndIndex(loot.length, landingIndex, RIGHT_PADDING_CARDS)
    );

    if (!winner || landingIndex >= copies.length) {
        return copies;
    }

    return copies.map((item: LootInterface, index: number) => index === landingIndex ? winner : item);
};

const LootboxDetailsCarousel = ({
    fading = false,
    loot,
    spinDelayMs = SPIN_DELAY_MS,
    winner
}: Props): React.ReactElement | null => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [delayPassed, setDelayPassed] = useState(false);
    const landingIndex = reelEndIndex(START_INDEX, loot.length, ROTATIONS);
    const reel = useMemo(() => reelWithWinner(loot, winner, landingIndex), [
        landingIndex,
        loot,
        winner
    ]);

    useEffect(() => {
        const timer = window.setTimeout(() => setDelayPassed(true), spinDelayMs);

        return () => window.clearTimeout(timer);
    }, [spinDelayMs]);

    useReelSpin(trackRef, {
        delayMs: 0,
        durationMs: SPIN_DURATION_MS,
        enabled: reel.length > 0 && delayPassed && Boolean(winner),
        endIndex: landingIndex,
        startIndex: START_INDEX,
        step: CARD_STEP
    });

    if (reel.length === 0) {
        return null;
    }

    return <div
        className={`lootbox-detail__carousel${fading ? ' lootbox-detail__carousel--out' : ''}`}
        aria-hidden="true"
    >
        <div className="lootbox-detail__carousel-marker"/>
        <div className="lootbox-detail__carousel-track" ref={trackRef}>
            {reel.map((item: LootInterface, index: number) => <PrizeCard
                key={`${lootRarity(item)}-${index}`}
                loot={item}
            />)}
        </div>
    </div>;
};

export default LootboxDetailsCarousel;
